import { useEffect, useRef, useState } from 'react';

interface InlineTextCellProps {
  value: string | null;
  placeholder?: string;
  multiline?: boolean;
  onCommit: (next: string | null) => void;
  disabled?: boolean;
}

function classes(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function InlineTextCell({
  value,
  placeholder = '—',
  multiline = false,
  onCommit,
  disabled = false,
}: InlineTextCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === value) return;
    onCommit(next);
  };

  const cancel = () => {
    setDraft(value ?? '');
    setEditing(false);
  };

  if (!editing) {
    const isEmpty = !value;
    return (
      <span
        className={classes(
          'editable-cell',
          isEmpty && 'is-empty',
          disabled && 'is-disabled',
        )}
        onClick={() => !disabled && setEditing(true)}
        title={disabled ? undefined : 'Klicken zum Bearbeiten'}
      >
        {value || placeholder}
      </span>
    );
  }

  if (multiline) {
    return (
      <textarea
        ref={(el) => (inputRef.current = el)}
        className="editable-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel();
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
        }}
        rows={2}
        style={{ minHeight: 48 }}
      />
    );
  }

  return (
    <input
      ref={(el) => (inputRef.current = el)}
      className="editable-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') cancel();
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

interface InlineDateCellProps {
  value: string | null;
  onCommit: (next: string | null) => void;
  disabled?: boolean;
}

/**
 * Parst Schweizer Datumseingaben tolerant:
 *   "1.1.26", "01.01.26", "01.01.2026"
 *   "1/1/26", "01-01-2026"
 *   "01012026", "010126"  (ohne Trenner)
 *   "1.1." oder "1.1"     (Jahr = aktuelles Jahr)
 *   ""                    (löscht Wert)
 * Liefert ISO-Datum "YYYY-MM-DD" oder null bei leerer Eingabe;
 * `undefined` bei ungültigem Format (= nicht commit-tauglich).
 */
function parseSwissDate(input: string): string | null | undefined {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const currentYear = new Date().getFullYear();
  let day: number, month: number, year: number;

  // Reine Ziffernfolgen (z.B. "010126" oder "01012026")
  if (/^\d+$/.test(trimmed)) {
    if (trimmed.length === 6) {
      day = Number(trimmed.slice(0, 2));
      month = Number(trimmed.slice(2, 4));
      year = 2000 + Number(trimmed.slice(4, 6));
    } else if (trimmed.length === 8) {
      day = Number(trimmed.slice(0, 2));
      month = Number(trimmed.slice(2, 4));
      year = Number(trimmed.slice(4, 8));
    } else {
      return undefined;
    }
  } else {
    // Mit Trennern (Punkt, Slash, Bindestrich)
    const parts = trimmed.split(/[./-]/);
    if (parts.length < 2 || parts.length > 3) return undefined;
    const [d, m, y] = parts;
    if (!d || !m || !/^\d{1,2}$/.test(d) || !/^\d{1,2}$/.test(m)) return undefined;
    day = Number(d);
    month = Number(m);
    if (!y) {
      year = currentYear;
    } else if (/^\d{2}$/.test(y)) {
      year = 2000 + Number(y);
    } else if (/^\d{4}$/.test(y)) {
      year = Number(y);
    } else {
      return undefined;
    }
  }

  if (month < 1 || month > 12) return undefined;
  if (day < 1 || day > 31) return undefined;

  // Roundtrip-Check: 31.02 würde sonst zum 03.03 werden — als ungültig ablehnen.
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return undefined;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** ISO-Datum "YYYY-MM-DD" → Schweizer Anzeige "DD.MM.YYYY" */
function isoToSwiss(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

/** Springt zur nächsten/vorigen Date-Zelle im DOM. Identifiziert via
 *  data-date-cell-Attribut. So funktioniert Tab/Enter über die vier
 *  Datums-Felder einer Aufgaben-Zeile hinweg, ohne Prop-Drilling. */
function focusAdjacentDateCell(current: HTMLElement, direction: 1 | -1): boolean {
  const cells = Array.from(
    document.querySelectorAll<HTMLElement>('[data-date-cell]:not([data-date-cell-disabled])'),
  );
  const idx = cells.indexOf(current);
  if (idx < 0) return false;
  const target = cells[idx + direction];
  if (!target) return false;
  target.focus();
  return true;
}

export function InlineDateCell({ value, onCommit, disabled }: InlineDateCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(isoToSwiss(value));
  const [parseError, setParseError] = useState(false);
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(isoToSwiss(value));
      setParseError(false);
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  /** Schreibt zurück. Liefert true wenn der Wert valid war (auch leer),
   *  false bei Parse-Fehler — Caller (Tab/Enter) entscheidet dann ob
   *  weiter navigiert wird. */
  const commit = (): boolean => {
    const parsed = parseSwissDate(draft);
    if (parsed === undefined) {
      setParseError(true);
      return false;
    }
    setParseError(false);
    setEditing(false);
    if (parsed !== value) onCommit(parsed);
    return true;
  };

  const cancel = () => {
    setDraft(isoToSwiss(value));
    setParseError(false);
    setEditing(false);
  };

  if (!editing) {
    const display = value ? isoToSwiss(value) : '—';
    return (
      <span
        ref={spanRef}
        data-date-cell
        data-date-cell-disabled={disabled ? '' : undefined}
        className={classes('editable-cell', !value && 'is-empty', disabled && 'is-disabled')}
        onClick={() => !disabled && setEditing(true)}
        // tabIndex=0 macht die Zelle tab-bar; onFocus öffnet automatisch
        // den Edit-Mode, sodass Tab durch alle Datums-Felder den Cursor
        // direkt ins jeweils nächste Eingabefeld setzt.
        tabIndex={disabled ? -1 : 0}
        onFocus={() => !disabled && setEditing(true)}
        title={disabled ? undefined : 'Datum setzen (DD.MM.JJ oder DD.MM.JJJJ)'}
        style={{ minWidth: 90 }}
      >
        {display}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      data-date-cell
      className={classes('editable-input', parseError && 'is-invalid')}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        if (parseError) setParseError(false);
      }}
      onBlur={() => {
        // Verlassen ohne Validität: zurücksetzen statt zu erzwingen,
        // damit Tab durch ungültig getippte Felder nicht hängenbleibt.
        if (!commit()) cancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const ok = commit();
          // Bei Erfolg: in nächste Date-Zelle springen. Wenn keine
          // mehr: Focus bleibt hier (Span im Read-Mode).
          if (ok) {
            const el = inputRef.current;
            if (el) focusAdjacentDateCell(el, 1);
          }
          return;
        }
        if (e.key === 'Tab') {
          // Tab/Shift-Tab triggert ohnehin onBlur → commit. Standard-
          // Verhalten kümmert sich dann um den nächsten tabbable.
          if (!commit()) {
            // Bei Parse-Fehler: nicht zur nächsten Zelle springen
            e.preventDefault();
          }
        }
      }}
      style={{ width: 100 }}
      placeholder="TT.MM.JJ"
    />
  );
}

interface InlineNumberCellProps {
  value: number | null;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  onCommit: (next: number | null) => void;
  disabled?: boolean;
}

export function InlineNumberCell({
  value,
  min,
  max,
  step = 1,
  suffix,
  onCommit,
  disabled,
}: InlineNumberCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value != null ? String(value) : '');

  useEffect(() => {
    if (!editing) setDraft(value != null ? String(value) : '');
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    let parsed: number | null = draft === '' ? null : Number(draft);
    if (parsed != null && Number.isNaN(parsed)) parsed = null;
    if (parsed != null) {
      if (min != null && parsed < min) parsed = min;
      if (max != null && parsed > max) parsed = max;
    }
    if (parsed === value) return;
    onCommit(parsed);
  };

  if (!editing) {
    return (
      <span
        className={classes('editable-cell', value == null && 'is-empty', disabled && 'is-disabled')}
        onClick={() => !disabled && setEditing(true)}
        title={disabled ? undefined : 'Klicken zum Bearbeiten'}
        style={{ minWidth: 56, justifyContent: 'flex-end' }}
      >
        {value != null ? `${value}${suffix ?? ''}` : '—'}
      </span>
    );
  }

  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      className="editable-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDraft(value != null ? String(value) : '');
          setEditing(false);
        }
        if (e.key === 'Enter') commit();
      }}
      autoFocus
      style={{ width: 84 }}
    />
  );
}
